import * as Immutable from 'immutable';
import moment from 'moment';
import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { Modal } from 'semantic-ui-react';

import { editAccountUser, setReferrer } from '../../actions/AccountActions/AccountActions';
import { getUserInformation } from '../../actions/AuthenticationActions';
import { addFlashMessages } from '../../actions/FlashMessagesActions';
import { Loader } from '../../components/Loader';
import { Referrer } from '../../components/Modals/Referrer';
import { localeData } from '../../locales/data';
import { StateType } from '../../reducers';
import { accountReferrerSelector } from '../../reducers/AccountReducer/AccountSelector';
import {
  getIsLoggedInSelector,
  getUserDataCampaignSelector,
  getUserDataDateCreatedSelector,
} from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import {
  competitionUserCompetitionResultsSelector,
  logoutCompetitionIdSelector,
} from '../../reducers/CompetitionsReducer/CompetitionsSelector';
import { getReferrerCampaign } from '../../shared/helpers';
import { formatMessageHelper } from '../../shared/helpers/internationalization';
import { logErrorService } from '../../shared/services';
import { DispatchConnect } from '../../types';

interface ReferrerModalState {
  email: { value: string; valid: boolean; errors: Immutable.List<any> };
  showModal: boolean;
  loading: boolean;
}

interface ReferrerModalProps {
  needsRecertification: boolean;
}

interface ReferrerModalStateProps {
  logoutCompetition: string;
  userCompetitions: any;
  userisLoggedIn: boolean;
  referrerSet: boolean;
  hasCampaign: undefined | string;
  dateCreated: string;
}

interface ReferrerModalStateDispatchers {
  dispatchEditReferrer: (data: any) => Promise<object>;
  dispatchAddFlashMessages: (message: any, options: any) => void;
  dispatchSetReferrer: () => void;
  dispatchGetUserInformation: () => void;
}

type ReferrerModalGenericProps = InjectedIntlProps & ReferrerModalStateProps & ReferrerModalStateDispatchers & ReferrerModalProps;

export const ReferrerModal = (Component: any) =>
  class extends React.Component<ReferrerModalGenericProps, ReferrerModalState> {
    state = {
      loading: false,
      showModal: true,
      email: { value: '', valid: false, errors: Immutable.List() },
    };

    invalidFieldError = 'field.userid.invalid';
    validationRegex = /^[A-Z]{2}\d{5}$/;

    handleModalClose = () => {
      this.setState({ showModal: false }, () => this.props.dispatchSetReferrer());
    };

    setReferreData() {
      const { value } = this.state.email;
      const { logoutCompetition, userCompetitions } = this.props;
      let status = false;
      let userCompetition: any = false;

      if (logoutCompetition && userCompetitions) {
        userCompetition = userCompetitions.find((competition: any) => competition.get('id') === logoutCompetition);
        status = userCompetition && userCompetition.get('status') === 'ACCEPTED';
      }

      return getReferrerCampaign(value, logoutCompetition, userCompetition, status, status);
    }

    handleSubmit = () => {
      const campaign = this.setReferreData();
      if (campaign) {
        const auxiliary = {
          auxiliary: {
            campaign,
            agree: ['account-details'],
          },
        };
        this.setState({ loading: true });

        this.props
          .dispatchEditReferrer(auxiliary)
          .then((json: any) => {
            if (json && json.type === 'EDIT_ACCOUNT_DATA') {
              this.props.dispatchAddFlashMessages(
                [{ msg: 'referrer.add.success', defaultMessage: localeData.en['referrer.add.success'] }],
                { type: 'info' }
              );
              this.props.dispatchGetUserInformation();
            }
            this.setState({ showModal: false, loading: false });
            this.props.dispatchSetReferrer();
          })
          .catch((error: any) => logErrorService(error, error));
      } else {
        this.setState({ showModal: false });
      }
    };

    handleNotReferred = () => {
      const auxiliary = {
        auxiliary: {
          campaign: {
            source: 'Individual',
            medium: 'referral',
            campaign: 'WebSim',
          },
          agree: ['account-details'],
        },
      };
      this.setState({ loading: true });

      this.props
        .dispatchEditReferrer(auxiliary)
        .then((json: any) => {
          if (json && json.type === 'EDIT_ACCOUNT_DATA') {
            this.props.dispatchGetUserInformation();
          }
          this.setState({ showModal: false, loading: false });
          this.props.dispatchSetReferrer();
        })
        .catch((error: any) => logErrorService(error, error));
    };

    userIdValidation(value: string): Immutable.List<any> {
      if (value.trim().length === 0) {
        return Immutable.List(['field.required']);
      } else if (value === undefined) {
        return Immutable.List([this.invalidFieldError]);
      } else {
        if (this.validationRegex.test(value) === false && this.state.email.errors.size !== 0) {
          return Immutable.List([this.invalidFieldError]);
        } else {
          return Immutable.List();
        }
      }
    }

    emailBlurValidation(value: string): Immutable.List<any> {
      if (value.trim().length === 0) {
        return Immutable.List(['field.required']);
      } else if (value === undefined) {
        return Immutable.List([this.invalidFieldError]);
      } else {
        if (this.validationRegex.test(value) === false) {
          return Immutable.List([this.invalidFieldError]);
        } else {
          return Immutable.List();
        }
      }
    }

    onChangeEmail = (_: any, data: any) => {
      const errors = this.userIdValidation(data.value);
      const valid = errors.size === 0;
      const value = data.value;

      this.setState({ email: { value, valid, errors } });
    };

    onBlurEmail = (dataInfo: any) => {
      const data = dataInfo.currentTarget;
      const errors = this.emailBlurValidation(data.value);
      const valid = errors.size === 0;
      const value = data.value;

      this.setState({ email: { value, valid, errors } });
    };

    shouldOpenModal = () => this.props.userisLoggedIn && this.props.referrerSet === false && this.checkUserDataShouldDisplayModal();

    // Check if user dateCreated >= 2019-02-04T00:00:00-05:00 and user.auxiliary.campaign.campaign is not null or empty
    checkUserDataShouldDisplayModal() {
      const dateValid = moment(this.props.dateCreated).isSameOrAfter('2019-02-04T00:00:00-05:00');
      const campaignValid = this.props.hasCampaign && this.props.hasCampaign !== '';

      return dateValid === true && !campaignValid;
    }

    render() {
      if (this.props.needsRecertification || !this.props.userisLoggedIn) {
        return <Component {...this.props} />;
      } else if (!this.props.dateCreated) {
        return <Loader />;
      } else if (this.props.userisLoggedIn && this.props.referrerSet === false && this.checkUserDataShouldDisplayModal()) {
        return (
          <React.Fragment>
            <Modal closeIcon={true} open={this.state.showModal} onClose={this.handleModalClose} size="tiny">
              <Modal.Content>
                {this.state.loading === false ? (
                  <Referrer
                    emailData={this.state.email}
                    onChangeEmail={this.onChangeEmail}
                    onBlurEmail={this.onBlurEmail}
                    handleSubmit={this.handleSubmit}
                    handleNotReferred={this.handleNotReferred}
                    title={formatMessageHelper(this.props.intl, 'referrer.modal.title')}
                    handleclose={this.handleModalClose}
                  />
                ) : (
                  <Loader modifiers={['small']} />
                )}
              </Modal.Content>
            </Modal>
            <Component {...this.props} />
          </React.Fragment>
        );
      } else {
        return <Component {...this.props} />;
      }
    }
  };

const mapStateToProps = (state: StateType): ReferrerModalStateProps => ({
  // @ts-ignore
  logoutCompetition: logoutCompetitionIdSelector(state),
  userCompetitions: competitionUserCompetitionResultsSelector(state),
  // @ts-ignore
  userisLoggedIn: getIsLoggedInSelector(state),
  // @ts-ignore
  referrerSet: accountReferrerSelector(state),
  // @ts-ignore
  hasCampaign: getUserDataCampaignSelector(state),
  // @ts-ignore
  dateCreated: getUserDataDateCreatedSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): ReferrerModalStateDispatchers => ({
  dispatchEditReferrer: async (data: any) =>
    dispatch(editAccountUser({ data: data, photoFile: null, showFlashMessage: true, resumeFile: null })),
  dispatchAddFlashMessages: (message: any, options: any) => dispatch(addFlashMessages(message, options)),
  dispatchSetReferrer: () => dispatch(setReferrer()),
  dispatchGetUserInformation: async () => dispatch(getUserInformation('self', false)),
});

export default compose<any>(
  connect<ReferrerModalStateProps, DispatchConnect<ReferrerModalStateDispatchers>, any, StateType>(mapStateToProps, mapDispatchToProps),
  injectIntl,
  ReferrerModal
);
